import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Typography,
  Button,
  Grid,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Avatar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Alert,
  CircularProgress,
  Tooltip,
  Divider,
  CardMedia,
  Tabs,
  Tab,
  Checkbox,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CloudUpload as UploadIcon,
  Link as LinkIcon,
  Visibility as ViewIcon,
  Star as StarIcon,
  Refresh as RefreshIcon,
  RestoreFromTrash as RestoreIcon,
  Image as ImageIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { bannerService } from '../../services/bannerService';
import { settingsService } from '../../services/settingsService';

const BannerManagement = () => {
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingBanner, setEditingBanner] = useState(null);
  const [activeTab, setActiveTab] = useState(0); // 0: Upload, 1: URL
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [bannerToDelete, setBannerToDelete] = useState(null);
  
  // Banner settings state
  const [bannerEnabled, setBannerEnabled] = useState(true);
  const [settingsLoading, setSettingsLoading] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    imageUrl: '',
    active: true,
    productName: '',
    category: '',
  });
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');
  
  // Bulk operations state
  const [selectedBanners, setSelectedBanners] = useState([]);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [selectAll, setSelectAll] = useState(false);

  // Load banners and settings on component mount
  useEffect(() => {
    loadBanners();
    loadSettings();
  }, []);

  const loadBanners = async () => {
    setLoading(true);
    try {
      const data = await bannerService.getAllBanners();
      setBanners(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error loading banners:', error);
      toast.error('Failed to load banners');
      setBanners([]);
    } finally {
      setLoading(false);
    }
  };

  const loadSettings = async () => {
    try {
      const settings = await settingsService.getAllSettings();
      const bannerSetting = settings.find(setting => setting.key === 'banner.enabled');
      setBannerEnabled(bannerSetting ? bannerSetting.value === 'true' : true);
    } catch (error) {
      console.error('Error loading settings:', error);
      toast.error('Failed to load banner settings');
    }
  };

  const handleBannerEnabledChange = async (event) => {
    const enabled = event.target.checked;
    setSettingsLoading(true);
    try {
      await settingsService.updateBannerSettings(enabled);
      setBannerEnabled(enabled);
      toast.success(`Banner carousel ${enabled ? 'enabled' : 'disabled'} successfully`);
    } catch (error) {
      console.error('Error updating banner settings:', error);
      toast.error('Failed to update banner settings');
    } finally {
      setSettingsLoading(false);
    }
  };

  const handleOpenDialog = (banner = null) => {
    if (banner) {
      setEditingBanner(banner);
      setFormData({
        title: banner.title || '',
        description: banner.description || '',
        imageUrl: banner.imageUrl || '',
        active: banner.active || false,
        productName: banner.productName || '',
        category: banner.category || '',
      });
      setPreviewUrl(banner.imageUrl || '');
      setActiveTab(1); // URL tab for editing
    } else {
      setEditingBanner(null);
      setFormData({
        title: '',
        description: '',
        imageUrl: '',
        active: true,
        productName: '',
        category: '',
      });
      setSelectedFile(null);
      setPreviewUrl('');
      setActiveTab(0); // Upload tab for new
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingBanner(null);
    setFormData({
      title: '',
      description: '',
      imageUrl: '',
      active: true,
      productName: '',
      category: '',
    });
    setSelectedFile(null);
    setPreviewUrl('');
  };

  const validateImageFile = (file) => {
    // File type validation
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Invalid file format. Please use JPEG, PNG, or WebP images.');
      return false;
    }

    // File size validation (max 5MB)
    const maxSize = 5 * 1024 * 1024; // 5MB in bytes
    if (file.size > maxSize) {
      toast.error('File size too large. Please use images smaller than 5MB.');
      return false;
    }

    return true;
  };

  const validateImageDimensions = (file) => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        const { width, height } = img;
        
        // Recommended dimensions for banner carousel
        const minWidth = 1200;
        const minHeight = 400;
        const maxWidth = 2400;
        const maxHeight = 1200;
        const aspectRatio = width / height;
        const idealAspectRatio = 3; // 3:1 ratio for banners
        
        if (width < minWidth || height < minHeight) {
          toast.error(`Image too small. Minimum dimensions: ${minWidth}x${minHeight}px`);
          resolve(false);
          return;
        }
        
        if (width > maxWidth || height > maxHeight) {
          toast.error(`Image too large. Maximum dimensions: ${maxWidth}x${maxHeight}px`);
          resolve(false);
          return;
        }
        
        if (Math.abs(aspectRatio - idealAspectRatio) > 0.5) {
          toast.warning(`Image aspect ratio is ${aspectRatio.toFixed(2)}:1. Recommended: 3:1 for optimal display.`);
        }
        
        resolve(true);
      };
      
      img.onerror = () => {
        toast.error('Invalid image file');
        resolve(false);
      };
      
      img.src = URL.createObjectURL(file);
    });
  };

  const handleFileChange = async (event) => {
    const file = event.target.files[0];
    if (file) {
      // Basic validation
      if (!validateImageFile(file)) {
        return;
      }

      // Dimension validation
      const isDimensionsValid = await validateImageDimensions(file);
      if (!isDimensionsValid) {
        return;
      }

      setSelectedFile(file);
      const url = URL.createObjectURL(file);
      setPreviewUrl(url);
      toast.success('Image validated successfully!');
    }
  };

  const handleUrlChange = (url) => {
    setFormData({ ...formData, imageUrl: url });
    setPreviewUrl(url);
  };

  const handleSubmit = async () => {
    if (!formData.title.trim()) {
      toast.error('Title is required');
      return;
    }

    if (activeTab === 0 && !selectedFile && !editingBanner) {
      toast.error('Please select an image file');
      return;
    }

    if (activeTab === 1 && !formData.imageUrl.trim()) {
      toast.error('Image URL is required');
      return;
    }

    setLoading(true);
    try {
      if (editingBanner) {
        // Update existing banner
        await bannerService.updateBanner(editingBanner.id, formData);
        toast.success('Banner updated successfully');
      } else {
        // Create new banner
        if (activeTab === 0) {
          // File upload
          const uploadFormData = new FormData();
          uploadFormData.append('file', selectedFile);
          uploadFormData.append('title', formData.title);
          uploadFormData.append('description', formData.description);
          uploadFormData.append('linkUrl', formData.linkUrl || '');
          uploadFormData.append('active', formData.active);
          if (formData.productName) {
            uploadFormData.append('productName', formData.productName);
          }
          if (formData.category) {
            uploadFormData.append('category', formData.category);
          }
          
          await bannerService.uploadBannerImage(uploadFormData);
        } else {
          // URL-based
          await bannerService.createBannerWithUrl(formData);
        }
        toast.success('Banner created successfully');
      }
      
      handleCloseDialog();
      loadBanners();
    } catch (error) {
      console.error('Error saving banner:', error);
      toast.error('Failed to save banner');
    } finally {
      setLoading(false);
    }
  };

  const handleActivate = async (id) => {
    setLoading(true);
    try {
      await bannerService.activateBanner(id);
      toast.success('Banner activated successfully');
      loadBanners();
    } catch (error) {
      console.error('Error activating banner:', error);
      toast.error('Failed to activate banner');
    } finally {
      setLoading(false);
    }
  };

  const handleDeactivate = async (id) => {
    setLoading(true);
    try {
      await bannerService.deactivateBanner(id);
      toast.success('Banner deactivated successfully');
      loadBanners();
    } catch (error) {
      console.error('Error deactivating banner:', error);
      toast.error('Failed to deactivate banner');
    } finally {
      setLoading(false);
    }
  };

  const handleResetToDefault = async () => {
    setLoading(true);
    try {
      await bannerService.resetToDefault();
      toast.success('Reset to default banner successfully');
      loadBanners();
    } catch (error) {
      console.error('Error resetting to default:', error);
      toast.error('Failed to reset to default banner');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (banner) => {
    setBannerToDelete(banner);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!bannerToDelete) return;
    
    setLoading(true);
    try {
      await bannerService.deleteBanner(bannerToDelete.id);
      toast.success('Banner deleted successfully');
      loadBanners();
    } catch (error) {
      console.error('Error deleting banner:', error);
      toast.error(error.response?.data?.error || 'Failed to delete banner');
    } finally {
      setLoading(false);
      setDeleteDialogOpen(false);
      setBannerToDelete(null);
    }
  };

  // Bulk operations handlers
  const handleSelectBanner = (bannerId) => {
    setSelectedBanners(prev => {
      if (prev.includes(bannerId)) {
        return prev.filter(id => id !== bannerId);
      } else {
        return [...prev, bannerId];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedBanners([]);
      setSelectAll(false);
    } else {
      setSelectedBanners(banners.map(banner => banner.id));
      setSelectAll(true);
    }
  };

  const handleActivateSelected = async () => {
    if (selectedBanners.length === 0) {
      toast.warning('Please select banners to activate');
      return;
    }

    setBulkLoading(true);
    try {
      await bannerService.activateMultipleBanners(selectedBanners);
      toast.success(`${selectedBanners.length} banner(s) activated successfully`);
      setSelectedBanners([]);
      setSelectAll(false);
      loadBanners();
    } catch (error) {
      console.error('Error activating multiple banners:', error);
      toast.error('Failed to activate selected banners');
    } finally {
      setBulkLoading(false);
    }
  };

  const handleDeactivateAll = async () => {
    setBulkLoading(true);
    try {
      await bannerService.deactivateAllBanners();
      toast.success('All banners deactivated successfully');
      setSelectedBanners([]);
      setSelectAll(false);
      loadBanners();
    } catch (error) {
      console.error('Error deactivating all banners:', error);
      toast.error('Failed to deactivate all banners');
    } finally {
      setBulkLoading(false);
    }
  };

  return (
    <Box>
      <Card>
        <CardHeader
          title="Banner Image Management"
          subheader="Manage homepage banner images with upload and URL options"
          action={
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="outlined"
                startIcon={<RestoreIcon />}
                onClick={handleResetToDefault}
                disabled={loading}
              >
                Reset to Default
              </Button>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={loadBanners}
                disabled={loading}
              >
                Refresh
              </Button>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => handleOpenDialog()}
                disabled={loading}
              >
                Add Banner
              </Button>
            </Box>
          }
        />
        
        {/* Banner Enable/Disable Toggle */}
        <Box sx={{ px: 3, py: 2, borderBottom: '1px solid #e0e0e0' }}>
          <FormControlLabel
            control={
              <Switch
                checked={bannerEnabled}
                onChange={handleBannerEnabledChange}
                disabled={settingsLoading}
                color="primary"
              />
            }
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body1" fontWeight="medium">
                  Enable Banner Carousel
                </Typography>
                {settingsLoading && <CircularProgress size={16} />}
              </Box>
            }
          />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {bannerEnabled 
              ? 'Banner carousel is currently displayed on the homepage' 
              : 'Banner carousel is hidden from the homepage'
            }
          </Typography>
        </Box>
        
        <CardContent>
          {loading && banners.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : banners.length === 0 ? (
            <Alert severity="info">
              No banners found. Create your first banner to get started.
            </Alert>
          ) : (
            <>
              {/* Bulk Operations */}
              <Box sx={{ mb: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  {selectedBanners.length} banner(s) selected
                </Typography>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleActivateSelected}
                  disabled={selectedBanners.length === 0 || bulkLoading}
                  startIcon={bulkLoading ? <CircularProgress size={16} /> : <CheckCircleIcon />}
                >
                  Activate Selected
                </Button>
                <Button
                  variant="outlined"
                  color="warning"
                  onClick={handleDeactivateAll}
                  disabled={bulkLoading}
                  startIcon={bulkLoading ? <CircularProgress size={16} /> : <CancelIcon />}
                >
                  Deactivate All
                </Button>
              </Box>
              
              <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectAll}
                        onChange={handleSelectAll}
                        indeterminate={selectedBanners.length > 0 && selectedBanners.length < banners.length}
                      />
                    </TableCell>
                    <TableCell>Preview</TableCell>
                    <TableCell>Title</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {banners.map((banner) => (
                    <TableRow key={banner.id}>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selectedBanners.includes(banner.id)}
                          onChange={() => handleSelectBanner(banner.id)}
                        />
                      </TableCell>
                      <TableCell>
                        <Avatar
                          src={banner.imageUrl}
                          variant="rounded"
                          sx={{ width: 80, height: 45 }}
                        >
                          <ImageIcon />
                        </Avatar>
                      </TableCell>
                      <TableCell>
                        <Typography variant="subtitle2">
                          {banner.title}
                          {banner.isDefault && (
                            <Chip
                              label="Default"
                              size="small"
                              color="primary"
                              sx={{ ml: 1 }}
                            />
                          )}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {banner.description}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={banner.active ? 'Active' : 'Inactive'}
                          color={banner.active ? 'success' : 'default'}
                          size="small"
                          icon={banner.active ? <CheckCircleIcon /> : <CancelIcon />}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={banner.imageUrl?.startsWith('http://localhost') ? 'Uploaded' : 'URL'}
                          variant="outlined"
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Tooltip title="View">
                            <IconButton
                              size="small"
                              onClick={() => window.open(banner.imageUrl, '_blank')}
                            >
                              <ViewIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Edit">
                            <IconButton
                              size="small"
                              onClick={() => handleOpenDialog(banner)}
                            >
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                          {!banner.active && (
                            <Tooltip title="Activate">
                              <IconButton
                                size="small"
                                onClick={() => handleActivate(banner.id)}
                                color="primary"
                              >
                                <StarIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                          {banner.active && (
                            <Tooltip title="Deactivate">
                              <IconButton
                                size="small"
                                onClick={() => handleDeactivate(banner.id)}
                                color="warning"
                              >
                                <CancelIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                          {!banner.isDefault && (
                            <Tooltip title="Delete">
                              <IconButton
                                size="small"
                                onClick={() => handleDeleteClick(banner)}
                                color="error"
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            </>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Banner Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingBanner ? 'Edit Banner' : 'Add New Banner'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            {!editingBanner && (
              <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)} sx={{ mb: 3 }}>
                <Tab icon={<UploadIcon />} label="Upload Image" />
                <Tab icon={<LinkIcon />} label="Image URL" />
              </Tabs>
            )}

            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Title"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  required
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  label="Description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  multiline
                  rows={3}
                  sx={{ mb: 2 }}
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.active}
                      onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                    />
                  }
                  label="Active"
                />
                
                {/* Product Name Field */}
                <TextField
                  fullWidth
                  label="Product Name (Optional)"
                  value={formData.productName}
                  onChange={(e) => setFormData({ ...formData, productName: e.target.value })}
                  placeholder="Enter product name to filter"
                  helperText="When 'Shop Now' is clicked, will filter products by this name"
                  sx={{ mt: 2 }}
                />
                
                {/* Category Selection */}
                <FormControl fullWidth sx={{ mt: 2 }}>
                  <InputLabel>Category (Optional)</InputLabel>
                  <Select
                    value={formData.category}
                    label="Category (Optional)"
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  >
                    <MenuItem value="">None</MenuItem>
                    <MenuItem value="Marble">Marble</MenuItem>
                    <MenuItem value="Granite">Granite</MenuItem>
                    <MenuItem value="Tiles">Tiles</MenuItem>
                    <MenuItem value="Sandstone">Sandstone</MenuItem>
                    <MenuItem value="Indian Marble">Indian Marble</MenuItem>
                    <MenuItem value="Quartz">Quartz</MenuItem>
                    <MenuItem value="Slate">Slate</MenuItem>
                    <MenuItem value="Kota stone">Kota stone</MenuItem>
                    <MenuItem value="Quartzite">Quartzite</MenuItem>
                  </Select>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                    When 'Shop Now' is clicked, will filter products by this category
                  </Typography>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} md={6}>
                {activeTab === 0 && !editingBanner && (
                  <Box>
                    <Typography variant="subtitle2" sx={{ mb: 2 }}>
                      Upload Image
                    </Typography>
                    
                    {/* Image Specifications */}
                    <Alert severity="info" sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        Image Requirements:
                      </Typography>
                      <Typography variant="body2" component="div">
                        • <strong>Format:</strong> JPEG, PNG, or WebP<br/>
                        • <strong>Size:</strong> Maximum 5MB<br/>
                        • <strong>Dimensions:</strong> 1200×400px to 2400×1200px<br/>
                        • <strong>Aspect Ratio:</strong> 3:1 recommended for optimal display<br/>
                        • <strong>Resolution:</strong> High quality for crisp display
                      </Typography>
                    </Alert>
                    
                    <Button
                      variant="outlined"
                      component="label"
                      startIcon={<UploadIcon />}
                      fullWidth
                      sx={{ mb: 2 }}
                    >
                      Choose Image File
                      <input
                        type="file"
                        hidden
                        accept="image/jpeg,image/jpg,image/png,image/webp"
                        onChange={handleFileChange}
                      />
                    </Button>
                    {selectedFile && (
                      <Typography variant="body2" color="text.secondary">
                        Selected: {selectedFile.name}
                      </Typography>
                    )}
                  </Box>
                )}
                
                {(activeTab === 1 || editingBanner) && (
                  <Box>
                    <Typography variant="subtitle2" sx={{ mb: 2 }}>
                      Image URL
                    </Typography>
                    <TextField
                      fullWidth
                      label="Image URL"
                      value={formData.imageUrl}
                      onChange={(e) => handleUrlChange(e.target.value)}
                      placeholder="https://example.com/image.jpg"
                      sx={{ mb: 2 }}
                    />
                  </Box>
                )}
                
                {previewUrl && (
                  <Box>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      Preview
                    </Typography>
                    <Card>
                      <CardMedia
                        component="img"
                        height="200"
                        image={previewUrl}
                        alt="Banner preview"
                        sx={{ objectFit: 'cover' }}
                      />
                    </Card>
                  </Box>
                )}
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={loading}
          >
            {loading ? <CircularProgress size={20} /> : (editingBanner ? 'Update' : 'Create')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the banner "{bannerToDelete?.title}"?
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={loading}
          >
            {loading ? <CircularProgress size={20} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BannerManagement;